import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInAnonymously,
  getRedirectResult,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { signInWithMicrosoft, createPendingUser } from '../services/microsoftAuth';
import { handleSSOTokenFromURL } from '../services/ssoService';
import type { EPRUser } from '../types';

interface AuthContextValue {
  firebaseUser: User | null;
  user: EPRUser | null;
  loading: boolean;
  // Pending approval states (Microsoft SSO users awaiting HR Admin approval)
  isPendingApproval: boolean;
  isRejected: boolean;
  pendingEmail: string | null;
  pendingDisplayName: string | null;
  // Auth methods
  signIn: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  signInAnon: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<EPRUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPendingApproval, setIsPendingApproval] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingDisplayName, setPendingDisplayName] = useState<string | null>(null);

  useEffect(() => {
    // 1. Handle SSO token from URL (cross-app login)
    handleSSOTokenFromURL().then((handled) => {
      if (handled) console.log('🔑 EPR: SSO token processed');
    });

    // Handle any pending redirect result from silent auth attempt
    getRedirectResult(auth).catch((err) => {
      console.debug('Silent auth redirect:', err?.code || err?.message);
    });

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      // Reset pending/rejected states on each auth change
      setIsPendingApproval(false);
      setIsRejected(false);
      setPendingEmail(null);
      setPendingDisplayName(null);

      // Treat ANY signed-in user the same — anonymous (Dev bypass) and
      // Microsoft-SSO both have a users/{uid} doc the gate logic reads.
      // Previously this condition was `fbUser && !fbUser.isAnonymous`
      // which silently skipped the user-doc read for anonymous users
      // → app stuck on skeleton forever after Dev sign-in.
      if (fbUser) {
        try {
          const snap = await getDoc(doc(db, 'users', fbUser.uid));
          if (snap.exists()) {
            const data = snap.data() as any;

            // Access gate — three blocking signals, any one is enough.
            // Order matches Account-Portal/docs/ACCESS_MODEL.md "3 layers":
            //   L2  isActive === false           → global kill switch
            //   L3a access_policy === 'no_access' → global override from Access tab radio
            //   L3b appDisabled.epr === true      → per-app block from App Login Access toggle
            // Any layer triggers immediate signOut so RoleRoute / dashboard
            // never paint with a blocked session.
            const isBlocked =
              data?.isActive === false ||
              data?.access_policy === 'no_access' ||
              data?.appDisabled?.epr === true;
            if (isBlocked) {
              console.warn('🚫 EPR access blocked for user — signing out:', fbUser.uid, {
                isActive: data?.isActive,
                access_policy: data?.access_policy,
                appDisabled_epr: data?.appDisabled?.epr,
              });
              await firebaseSignOut(auth);
              setUser(null);
              setLoading(false);
              return;
            }

            // Per-app role resolution: prefer Account Portal's appRoles.epr,
            // fall back to flat `role` for users invited before per-app roles
            // existed. This makes Account Portal the source of truth — admin
            // can grant `appRoles.epr = 'hr_admin'` without touching OKR role.
            const eprRole = data?.appRoles?.epr ?? data?.role;
            setUser({
              id: snap.id,
              uid: fbUser.uid,
              ...data,
              role: eprRole,
            } as EPRUser);
          } else {
            // No users/{uid} doc — check if this is a Microsoft SSO user
            const isMicrosoftUser = fbUser.providerData.some(
              (p) => p.providerId === 'microsoft.com'
            );

            if (isMicrosoftUser) {
              // Check pendingUsers status
              const pendingSnap = await getDoc(doc(db, 'pendingUsers', fbUser.uid));

              if (pendingSnap.exists()) {
                const status = pendingSnap.data()?.status as string;
                const storedName =
                  pendingSnap.data()?.displayName || fbUser.displayName || fbUser.email || '';
                const storedEmail = pendingSnap.data()?.email || fbUser.email || '';

                if (status === 'rejected') {
                  // Force sign out and show rejected page
                  await firebaseSignOut(auth);
                  setIsRejected(true);
                  setPendingEmail(storedEmail);
                  setPendingDisplayName(storedName);
                  setUser(null);
                  setLoading(false);
                  return;
                }
                // status === 'pending' — already requested, show waiting page
                setIsPendingApproval(true);
                setPendingEmail(storedEmail);
                setPendingDisplayName(storedName);
              } else {
                // First time — create pending request
                const name = fbUser.displayName || fbUser.email?.split('@')[0] || '';
                const email = fbUser.email || '';
                await createPendingUser(fbUser.uid, email, name, fbUser.photoURL || undefined);
                setIsPendingApproval(true);
                setPendingEmail(email);
                setPendingDisplayName(name);
              }
            } else {
              console.warn('EPR AuthContext: user document not found for', fbUser.uid);
            }
            setUser(null);
          }
        } catch (err) {
          console.error('EPR AuthContext: failed to load user profile', err);
          setUser(null);
        }
      } else {
        // Anonymous visit. Do NOT auto-attempt Microsoft silent SSO (prompt=none):
        // - When it succeeded silently it skipped the login page entirely.
        // - When the redirect failed to navigate (headless, blocked, CSP) it left
        //   loading=true forever and PrivateRoute rendered a stuck skeleton.
        // Cross-app SSO via ?sso_token= URL param (handleSSOTokenFromURL above)
        // is the supported silent path; direct visits land on /login and the
        // user clicks the Microsoft button explicitly.
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithMicrosoft = async () => {
    // Throws ACCOUNT_LINK_REQUIRED error — caller (LoginPage) handles it
    await signInWithMicrosoft();
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setIsPendingApproval(false);
    setIsRejected(false);
    setPendingEmail(null);
    setPendingDisplayName(null);
  };

  const signInAnon = async () => {
    await signInAnonymously(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        isPendingApproval,
        isRejected,
        pendingEmail,
        pendingDisplayName,
        signIn,
        loginWithMicrosoft,
        signOut,
        signInAnon,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
